import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useAppDispatch } from "../../redux/hooks";
import { addTemplate } from "../../redux/entities/formBuilderEntity";
import { useNavigate } from "react-router-dom";
import useModalStrip from "../../global-hooks/useModalStrip";
import { generateID } from "../../utils/common";
import moment from "moment";

const textboxStyle = {
  minWidth: "100%",
  maxWidth: "100%",
  marginTop: "20px",
};

const NewFormDialogComponent = ({ openDialog, setOpenDialog }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showModalStrip } = useModalStrip();

  const [uploadedFile, setUploadedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [creatingForm, setCreatingForm] = useState(false);

  const [newFormData, setNewFormData] = useState({
    formName: "",
  });

  // Handle input change for form name
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file change for logo upload
  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url); // Set preview URL for the uploaded file
      setUploadedFile(file); // Store the file object
    }
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {

    e.preventDefault();

    // Validation for form name and logo
    if (["", null, undefined].includes(newFormData?.formName?.trim())) {
      showModalStrip("danger", "Form name cannot be empty", 5000);
      return;
    }
    try {
      setCreatingForm(true);

      // Create FormData object with form details
      let formData = new FormData();
      const dataToSend = {
        id: generateID(),
        "formName": newFormData.formName,
        createdAt: moment().unix() * 1000,
        creator: "Test User",
        formLayoutComponents: [],
        lastPublishedAt: 0,
        publishHistory: [],
        publishStatus: "draft",
        updatedAt: 0,
      }
      let isFileAttached = false

      if (uploadedFile) {
        isFileAttached = true;
        formData.append("logo", uploadedFile);
        formData.append("templateData", JSON.stringify(dataToSend))
      } else {
        formData = dataToSend;
      }

      // Dispatch action to add the new template
      const template = await dispatch(addTemplate({ formData, isFileAttached })).unwrap();
      // Navigate to the form builder page for the new template
      navigate(`/form-builder/${template.id}`);
      setOpenDialog(false);
    } catch (error) {
      showModalStrip("danger", "Error occurred while creating the form", 5000);
    } finally {
      setCreatingForm(false);
      resetForm();
    }
  };

  // Reset the form state
  const resetForm = () => {
    setNewFormData({ formName: "" });
    setUploadedFile(null);
    setFilePreviewUrl(null);
  };

  return (
    <Dialog
      open={openDialog}
      fullWidth
      maxWidth="lg"
      onClose={() => {
        setOpenDialog(false);
        resetForm();
      }}
    >
      <DialogTitle>
        <div className="d-flex align-items-center justify-content-between">
          <span
            style={{ padding: "9px", cursor: "pointer" }}
            onClick={() => {
              setOpenDialog(false);
              resetForm();
            }}
          >
            <i className="fas fa-arrow-left"></i>
          </span>
          <span
            style={{ padding: "9px", cursor: "pointer" }}
            onClick={() => {
              setOpenDialog(false);
              resetForm();
            }}
          >
            <i className="fas fa-times"></i>
          </span>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="p-30" style={{ minHeight: "500px" }}>
          <div
            style={{
              maxWidth: "360px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <h4>Enter the following details:</h4>
            <form onSubmit={handleFormSubmit} style={{ minWidth: "100%" }}>
              {/* Form Name Input */}
              <div>
                <TextField
                  label="Form Name"
                  name="formName"
                  value={newFormData.formName}
                  onChange={handleInputChange}
                  style={textboxStyle}
                  required
                />
              </div>

              {/* Logo Upload */}
              <div className="mt-4">
                <h4>Upload Logo:</h4>
                <input
                  id="file-upload"
                  style={{ display: "none" }}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="file-upload"
                  className="control-input-trigger-buttons"
                  style={{ cursor: "pointer" }}
                >
                  <i className="fas fa-cloud-upload-alt"></i>
                </label>
                {uploadedFile && (
                  <>
                    <p>Uploaded File: {uploadedFile.name}</p>
                    <img
                      src={filePreviewUrl}
                      alt="Uploaded"
                      width={150}
                      height={150}
                      style={{ marginTop: "10px" }}
                    />
                  </>
                )}
              </div>

              {/* Submit and Cancel Buttons */}
              <div className="mt-4">
                <button
                  className="btn btn-light btn-shadow m-t-20 m-r-10"
                  type="submit"
                  disabled={creatingForm}
                >
                  {creatingForm ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm mr-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading
                    </>
                  ) : (
                    "Create Form"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-light btn-shadow m-t-20 m-l-0"
                  disabled={creatingForm}
                  onClick={() => {
                    setOpenDialog(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewFormDialogComponent;
